import { useTranslation } from "react-i18next"

import { cn } from "@/lib/cn"

export interface OnlineDotProps {
  /**
   * The API's own `online` flag (the device schema's overdue-heartbeat
   * rule, LLD §7.6's "one definition of offline"). Shown as given whenever
   * it is present -- the fallback below is for a shape that doesn't carry
   * it, which the spec doesn't have today, but a defensive component
   * shouldn't assume it always will.
   */
  online?: boolean | null
  lastHeartbeat?: string | null
  heartbeatIntervalMinutes?: number | null
  className?: string
}

function isOnline({ online, lastHeartbeat, heartbeatIntervalMinutes }: OnlineDotProps): boolean {
  if (typeof online === "boolean") return online
  if (!lastHeartbeat || !heartbeatIntervalMinutes) return false
  const elapsedMs = Date.now() - new Date(lastHeartbeat).getTime()
  return elapsedMs <= 2 * heartbeatIntervalMinutes * 60_000
}

/**
 * A small status dot for a device row, never colour alone: the accessible
 * name ("Online"/"Offline") is what actually carries the state, to a screen
 * reader and, via `title`, to a sighted user who can't rely on colour either
 * (LLD §8.1). Falls back to "heartbeat within two intervals" only when
 * `online` itself is absent.
 */
export function OnlineDot(props: OnlineDotProps) {
  const { t } = useTranslation("common")
  const online = isOnline(props)
  const label = online ? t("onlineDot.online") : t("onlineDot.offline")

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        online ? "bg-success" : "bg-muted-foreground/50",
        props.className,
      )}
    />
  )
}
